import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { IChangeset } from '../interfaces';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { map, Observable } from 'rxjs';
import {
  toDataSourceRequestString,
  translateDataSourceResultGroups,
} from '@progress/kendo-data-query';
import { DateHelper, GridState } from '@shared';

interface IChangesetPayload {
  id: string;
  scheduledDate: string | null;
  type: string;
  restaurantName?: string;
  tierName?: string;
}

interface IScheduledChangeset {
  id: string;
  scheduledDate: Date | null;
  type: string;
  restaurantName?: string;
  tierName?: string;
}
@Injectable()
export class ChangesetService {
  private apiUrl: string = environment.apiUrl + '/changeset';

  constructor(private httpClient: HttpClient) {}

  downloadBackup(labOnly: boolean, type: string): Observable<Blob> {
    return this.httpClient.get(
      `${this.apiUrl}/${type}-backup?labOnly=${labOnly}`,
      {
        responseType: 'blob',
      },
    );
  }

  getPendingChanges(type: string, labOnly: boolean) {
    return this.httpClient.get<IChangeset[]>(
      `${this.apiUrl}/pending/${type}?labOnly=${labOnly}`,
    );
  }

  getAllChangesets(state: GridState): Observable<GridDataResult> {
    const hasGroups = state.group && state.group.length;
    return this.httpClient
      .get<GridDataResult>(
        `${this.apiUrl}?${toDataSourceRequestString(state)}`,
        {},
      )
      .pipe(
        map(({ data, total }) => ({
          data: hasGroups ? translateDataSourceResultGroups(data) : data,
          total: total,
        })),
      );
  }

  schedulePendingChanges(changesets: IScheduledChangeset[], labOnly: boolean) {
    const changesetsPayloadArray = [] as IChangesetPayload[];
    changesets.forEach((change) => {
      changesetsPayloadArray.push({
        ...change,
        scheduledDate: DateHelper.toLocalISOString(change.scheduledDate),
      });
    });

    return this.httpClient.post<IChangesetPayload[]>(
      `${this.apiUrl}/schedule?labOnly=${labOnly}`,
      changesetsPayloadArray,
    );
  }

  cancelPendingChange(changesetId: string, changeType: string) {
    return this.httpClient.delete(
      `${this.apiUrl}/${changeType}/${changesetId}`,
    );
  }

  rescheduleChangeset(changesetIds: string[], scheduledDate: Date) {
    return this.httpClient.post(`${this.apiUrl}/reschedule`, {
      changesetIds,
      scheduledDate,
    });
  }

  getPriceMismatches(
    tierId: string,
    state: GridState,
  ): Observable<GridDataResult> {
    const hasGroups = state.group && state.group.length;
    return this.httpClient
      .get<GridDataResult>(
        `${this.apiUrl}/pricemismatches/${tierId}?${toDataSourceRequestString(state)}`,
        {},
      )
      .pipe(
        map(({ data, total }) => ({
          data: hasGroups ? translateDataSourceResultGroups(data) : data,
          total: total,
        })),
      );
  }

  cancelChangeset(changesetId: string) {
    return this.httpClient.get(`${this.apiUrl}/cancel/${changesetId}`);
  }

  fixPriceMismatch(kingRestaurantId: number, mismatch: any) {
    return this.httpClient.post(
      `${this.apiUrl}/align/${kingRestaurantId}`,
      mismatch,
    );
  }

  alignTier(tierId: string, state: GridState) {
    return this.httpClient.get(
      `${this.apiUrl}/aligntier/${tierId}?${toDataSourceRequestString(state)}`,
    );
  }

  exportPriceMismatches(tierId: string): Observable<Blob> {
    return this.httpClient.get(
      `${this.apiUrl}/exportpricemismatches/${tierId}`,
      {
        responseType: 'blob',
      },
    );
  }

  exportAllMismatches(): Observable<Blob> {
    return this.httpClient.get(`${this.apiUrl}/exportpricemismatches/all`, {
      responseType: 'blob',
    });
  }

  getFailedChangesets(
    state: GridState,
    restaurantId: string | undefined,
    reportingUnit: number | undefined,
  ): Observable<GridDataResult> {
    var request;
    const hasGroups = state.group && state.group.length;

    if (reportingUnit == undefined || reportingUnit == null) {
      request = this.httpClient.get<GridDataResult>(
        `${this.apiUrl}/failed?${toDataSourceRequestString(state)}`,
      );
    } else if (restaurantId == undefined || restaurantId == null) {
      request = this.httpClient.get<GridDataResult>(
        `${this.apiUrl}/failed?reportingUnit=${reportingUnit}&${toDataSourceRequestString(state)}`,
      );
    } else {
      request = this.httpClient.get<GridDataResult>(
        `${this.apiUrl}/failed?reportingUnit=${reportingUnit}&restaurantId=${restaurantId}&${toDataSourceRequestString(state)}`,
      );
    }

    return request.pipe(
      map(({ data, total }) => ({
        data: hasGroups ? translateDataSourceResultGroups(data) : data,
        total: total,
      })),
    );
  }

  getChangesetExceptions(state: GridState): Observable<GridDataResult> {
    return this.httpClient.get<GridDataResult>(
      `${this.apiUrl}/exceptions?${toDataSourceRequestString(state)}`,
      {},
    );
  }

  removePendingChanges(type: string, tierId: number) {
    return this.httpClient.delete(
      `${this.apiUrl}/remove/${type}?tierId=${tierId}`,
    );
  }

  cancelAvailabilityChangesets(restaurantIds: string[]){
    return this.httpClient.put(
      `${this.apiUrl}/cancelavailability?restaurants=${restaurantIds}`,
      {},
    );
  }
}
